import { useMemo, useCallback } from 'react'
import { useResizeDetector } from 'react-resize-detector'
import { numberFormat } from 'shared/util'

import ReactEChartsCore from 'echarts-for-react/lib/core'
import * as echarts from 'echarts/core'
import { TreemapChart } from 'echarts/charts'
import { CanvasRenderer } from 'echarts/renderers'

import {
  TooltipComponent,
  GridComponent,
  LegendComponent
} from 'echarts/components'

import {
  Box, BoxProps, Alert,
  Stack, Typography,
  useTheme
} from "@mui/material"

import { SisetuDoctorsConditions } from '../condition'
import { GakkaiStaticticsRecord } from './data'

echarts.use([
  TooltipComponent,
  GridComponent,
  LegendComponent,
  TreemapChart,
  CanvasRenderer
])

type GakkaiGraphProps = {
  sisetuCode: string
  dataList?: GakkaiStaticticsRecord[]
  condition: SisetuDoctorsConditions
  onChangeCondition: (axis: 'gakkai', condition: { code: string, name: string }) => void
} & BoxProps
export const GakkaiGraph = ({
  dataList,
  condition,
  onChangeCondition,
  ...others
}: GakkaiGraphProps) => {
  const theme = useTheme()
  const { width, height, ref } = useResizeDetector()

  const selectedItemStyle = useMemo(
    () => ({
      color: theme.palette.primary.main
    }),
    [theme]
  )
  const unSelectedItemStyle = useMemo(
    () => ({
      color: theme.palette.action.disabled
    }),
    [theme]
  )

  const summary = useMemo<{
    key: string
    value: number
    name: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    itemStyle?: any
  }[]>(
    () => dataList?.flatMap(
      (record) => (record.gakkaiCode !== '99' && record.gakkaiName !== '') ? {
        key: record.gakkaiCode,
        value: record.kojinCount,
        name: record.gakkaiName,
        itemStyle: condition.gakkai ? (
          condition.gakkai.code === record.gakkaiCode ? selectedItemStyle : unSelectedItemStyle
        ) : undefined
      } : []
    ) || [],
    [dataList, condition.gakkai, selectedItemStyle, unSelectedItemStyle]
  )
  
  const valueFormatter = useCallback(
    (value: number | string) => `${value}人`,
    []
  )
  const labelFormatter = useCallback(
    (params: {
      name: string
      value: number
    }) => [
      `{name|${params.name}}`,
      '{hr|}',
      `{budget|${numberFormat(params.value)}人}`
    ].join('\n'),
    []
  )
  const onClickBox = useCallback(
    (params: { data?: { key?: string, name?: string }}) => {
      const data = params?.data
      if (data && data.key) {
        onChangeCondition('gakkai', { code: data.key, name: data.name + '学会' })
      }
    },
    [onChangeCondition]
  )

  const option = useMemo(
    () => ({
      //animation: false,
      tooltip: {
        trigger: 'item',
        valueFormatter
      },
      series: {
        type: 'treemap',
        nodeClick: undefined,
        roam: false,
        top: 8 + 28,
        bottom: 8,
        left: 8, right: 8,
        breadcrumb: {
          show: false
        },
        label: {
          show: true,
          position: 'insideTopLeft',
          formatter: labelFormatter,
          rich: {
            name: {
              fontSize: 12,
              color: '#fff'
            },
            hr: {
              width: '100%',
              borderColor: 'rgba(255,255,255,0.2)',
              borderWidth: 0.5,
              height: 0,
              lineHeight: 10
            },
            budget: {
              fontSize: 15,
              lineHeight: 30,
              color: '#fff'
            },
          }
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        },
        data: summary
      },
      textStyle: {
        fontFamily: 'Noto Sans JP,sans-serif'
      }
    }),
    [summary, valueFormatter, labelFormatter]
  )

  return (
    <Box ref={ref} width="100%" height="100%" position="relative" {...others}>
      <Stack
        direction="column"
        sx={{
          position: 'absolute',
          zIndex: 2,
          boxSizing: 'border-box',
          width: '100%',
        }}
        padding={1}
        spacing={1}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography
            variant="body1"
            component="div"
            color="text.primary"
            sx={{ textAlign: 'left', flexGrow: 1 }}
          >
            所属学会
          </Typography>
        </Stack>
        {dataList?.length === 0 && (
          <Alert severity="info">対象が見つかりません.</Alert>
        )}
      </Stack>

      <Box sx={{ flexGrow: 1, position: 'relative' }}>
        {width && height && (
          <ReactEChartsCore
            echarts={echarts}
            notMerge={true}
            onEvents={{
              'click': onClickBox
            }}
            option={option}
            opts={{
              renderer: 'canvas',
              width: 'auto'
            }}
            style={{
              width, height
            }}
            theme="insite"
          />
        )}
     </Box>
    </Box>
  )
}